import type { JSX } from 'preact';

/**
 * Les icônes, **écrites à la main**.
 *
 * La V1 tirait `lucide-react` ; ici une bibliothèque d'icônes coûterait quelques
 * dizaines de kilo-octets et une dépendance de plus qui s'exécute dans la page où
 * vivent les jetons (D37). Douze traits de SVG suffisent, et ils héritent de
 * `currentColor` — donc du thème, sans une ligne de plus.
 *
 * Elles sont **toujours `aria-hidden`** : une icône ne porte jamais le nom
 * accessible d'un contrôle, c'est le rôle de son libellé ou de son `aria-label`.
 */

type Props = { readonly size?: number; readonly class?: string };

function svg(path: JSX.Element, { size = 20, class: cls }: Props): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
      class={cls}
    >
      {path}
    </svg>
  );
}

export const IconSpace = (p: Props = {}) =>
  svg(<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />, p);

export const IconGallery = (p: Props = {}) =>
  svg(
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="m21 16-5-5-6 6-2-2-5 5" />
    </>,
    p,
  );

export const IconShared = (p: Props = {}) =>
  svg(
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 5.5a3 3 0 0 1 0 5.5M18 20a6 6 0 0 0-3-5.2" />
    </>,
    p,
  );

export const IconMe = (p: Props = {}) =>
  svg(
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>,
    p,
  );

export const IconSun = (p: Props = {}) =>
  svg(
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>,
    p,
  );

export const IconMoon = (p: Props = {}) =>
  svg(<path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5Z" />, p);

/**
 * **Deux arcs et deux pointes** — la flèche circulaire, et non le sablier ou les
 * deux flèches opposées de la synchronisation. Ce qu'on demande au serveur est
 * « redis-moi où on en est », pas « accorde-toi avec moi » : le second dessin
 * promettrait une écriture, qui n'a pas lieu.
 */
export const IconRefresh = (p: Props = {}) =>
  svg(
    <>
      <path d="M20 12a8 8 0 1 1-2.34-5.66" />
      <path d="M20 4v4.5h-4.5" />
    </>,
    p,
  );

export const IconAuto = (p: Props = {}) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 3.5v17" />
      <path d="M12 3.5a8.5 8.5 0 0 1 0 17Z" fill="currentColor" stroke="none" />
    </>,
    p,
  );

export const IconOffline = (p: Props = {}) =>
  svg(
    <>
      <path d="M2 2l20 20" />
      <path d="M5 12.5a7 7 0 0 1 4-2.3M9.5 6.2A9 9 0 0 1 19 9.5" />
      <path d="M12 18h.01" />
    </>,
    p,
  );

export const IconTrash = (p: Props = {}) =>
  svg(
    <>
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
    </>,
    p,
  );

/**
 * **La flèche qui revient en arrière** — l'inverse exact d'`IconRefresh`, dont
 * elle reprend l'arc en le parcourant dans l'autre sens. Restaurer n'est pas
 * actualiser : le dessin doit dire « ceci revient d'où c'est parti », pas
 * « redis-moi où on en est ».
 */
export const IconRestore = (p: Props = {}) =>
  svg(
    <>
      <path d="M4 12a8 8 0 1 0 2.34-5.66" />
      <path d="M4 4v4.5h4.5" />
    </>,
    p,
  );

export const IconClose = (p: Props = {}) => svg(<path d="M6 6l12 12M18 6L6 18" />, p);

export const IconPrev = (p: Props = {}) => svg(<path d="M15 5l-7 7 7 7" />, p);

/* Les quatre coins qui s'écartent, et les quatre qui reviennent : c'est le dessin
   que tous les lecteurs vidéo emploient, et le seul que l'œil lise sans légende. */
export const IconFullscreen = (p: Props = {}) =>
  svg(<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />, p);

export const IconFullscreenExit = (p: Props = {}) =>
  svg(<path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />, p);

export const IconNext = (p: Props = {}) => svg(<path d="M9 5l7 7-7 7" />, p);

export const IconOpen = (p: Props = {}) =>
  svg(
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4l-9 9" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </>,
    p,
  );

/**
 * ── 4.5 bis ─────────────────────────────────────────────────────────────────
 *
 * « Beaucoup de boutons en texte brut ; unifie par rapport à l'app. » Le jeu qui
 * suit est donc choisi **contre les icônes Material de l'app**, pas librement :
 * `folder` / `folder_open`, `chevron_right`, `note_add`, `upload_file`,
 * `playlist_add`, `remove_circle_outline`, `info_outline`, `create_new_folder`.
 * Deux applications qui nomment le même geste par deux dessins différents
 * obligent à réapprendre en changeant d'écran.
 */

export const IconFolder = (p: Props = {}) =>
  svg(<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />, p);

export const IconFolderOpen = (p: Props = {}) =>
  svg(
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v1" />
      <path d="M3 19V9h18l-2.2 8.5a2 2 0 0 1-1.9 1.5H5a2 2 0 0 1-2-2Z" />
    </>,
    p,
  );

export const IconNewFolder = (p: Props = {}) =>
  svg(
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="M12 10v6M9 13h6" />
    </>,
    p,
  );

/** Le chevron **ne tourne pas ici** : la rotation est en CSS, sur `[aria-expanded]`. */
export const IconChevron = (p: Props = {}) => svg(<path d="M9 6l6 6-6 6" />, p);

/** Le chevron **vers le bas** : celui d'un contrôle qui déploie une liste. */
export const IconCaret = (p: Props = {}) => svg(<path d="m6 9 6 6 6-6" />, p);

/** Les chevrons du code source — « montre-moi la syntaxe ». */
/* Les deux maillons de chaîne : le dessin universel du lien, dans toutes les
   barres de mise en forme du monde. */
export const IconLink = (p: Props = {}) =>
  svg(
    <>
      <path d="M10.5 13.5a4 4 0 0 0 5.7 0l2.3-2.3a4 4 0 0 0-5.7-5.7l-1.1 1.1" />
      <path d="M13.5 10.5a4 4 0 0 0-5.7 0l-2.3 2.3a4 4 0 0 0 5.7 5.7l1.1-1.1" />
    </>,
    p,
  );

export const IconCode = (p: Props = {}) =>
  svg(<path d="m9 8-4 4 4 4M15 8l4 4-4 4" />, p);

/** L'entonnoir des filtres — le seul dessin que tout le monde lit sans légende. */
export const IconFilter = (p: Props = {}) => svg(<path d="M4 5h16l-6.4 7.6V19l-3.2-2v-4.4Z" />, p);

/** **Deux cadres qui se recouvrent** *(K4)* — la même image, deux fois. */
export const IconDuplicate = (p: Props = {}) =>
  svg(
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </>,
    p,
  );

export const IconPlus = (p: Props = {}) => svg(<path d="M12 5v14M5 12h14" />, p);

export const IconNote = (p: Props = {}) =>
  svg(
    <>
      <path d="M5 4h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
      <path d="M14 4v5h5" />
      <path d="M8 13h7M8 17h5" />
    </>,
    p,
  );

export const IconDoc = (p: Props = {}) =>
  svg(
    <>
      <path d="M5 4h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
      <path d="M14 4v5h5" />
    </>,
    p,
  );

export const IconUpload = (p: Props = {}) =>
  svg(
    <>
      <path d="M12 16V4" />
      <path d="M7.5 8.5 12 4l4.5 4.5" />
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </>,
    p,
  );

/* **« Il reste quelque chose à envoyer. »** Un nuage et une flèche qui monte, et
   non une disquette : le texte ne part pas sur un disque mais chez le serveur,
   et c'est ce trajet-là qui est en attente. Elle ne s'affiche que sur une note
   modifiée — une coche dit « c'est enregistré », ce qui est le contraire. */
export const IconCloudUp = (p: Props = {}) =>
  svg(
    <>
      <path d="M7 18a4 4 0 0 1-.4-8A5.5 5.5 0 0 1 17.3 9.6 3.7 3.7 0 0 1 17 18" />
      <path d="M12 21v-8M9 16l3-3 3 3" />
    </>,
    p,
  );

/**
 * **Envoyer vers une autre application** — la boîte et la flèche qui en sort.
 *
 * C'est le glyphe de partage d'iOS, et il est lu partout : la flèche quitte le
 * cadre, ce qui est exactement le geste. Le nœud-à-trois-points d'Android dit la
 * même chose mais se confond, à 18 px, avec le « … » qui **ouvre** le menu où
 * cette entrée vit.
 */
export const IconSend = (p: Props = {}) =>
  svg(
    <>
      <path d="M12 3v13" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 13v6a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-6" />
    </>,
    p,
  );

export const IconDownload = (p: Props = {}) =>
  svg(
    <>
      <path d="M12 4v12" />
      <path d="M7.5 11.5 12 16l4.5-4.5" />
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </>,
    p,
  );

/**
 * **L'appareil photo** — l'entrée d'ajout qui n'existait pas sur le site alors
 * qu'elle est la première de la feuille d'ajout de l'app. Sur un téléphone,
 * l'`accept` et le `capture` de l'entrée de fichiers suffisent à l'ouvrir.
 */
export const IconCamera = (p: Props = {}) =>
  svg(
    <>
      <path d="M4 8a2 2 0 0 1 2-2h1.5l1.2-1.8A1 1 0 0 1 9.5 3.8h5a1 1 0 0 1 .8.4L16.5 6H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
      <circle cx="12" cy="12.5" r="3.2" />
    </>,
    p,
  );

/** Le média mobile : une photo **et** une vidéo, puisque l'entrée porte les deux. */
export const IconMedia = (p: Props = {}) =>
  svg(
    <>
      <rect x="3" y="5" width="12" height="14" rx="2" />
      <circle cx="7" cy="9.5" r="1.3" />
      <path d="m15 15-3.5-3.5L3 19" />
      <path d="M18 8.5 21 7v10l-3-1.5Z" />
    </>,
    p,
  );

/**
 * **Le retour** — une flèche, et non le chevron `IconPrev` de la visionneuse :
 * les deux gestes ne sont pas le même, et deux dessins voisins pour « précédent »
 * et « revenir » feraient hésiter là où il ne doit pas y avoir d'hésitation.
 */
export const IconBack = (p: Props = {}) =>
  svg(
    <>
      <path d="M19 12H5" />
      <path d="m11 6-6 6 6 6" />
    </>,
    p,
  );

/** Une collection : des éléments empilés, et non un dossier — la forme dit la règle. */
export const IconCollection = (p: Props = {}) =>
  svg(
    <>
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M6 5h12M8 2.5h8" />
    </>,
    p,
  );

export const IconAddToCollection = (p: Props = {}) =>
  svg(
    <>
      <path d="M3 6h12M3 11h12M3 16h7" />
      <path d="M17 12v8M13 16h8" />
    </>,
    p,
  );

export const IconRemoveFrom = (p: Props = {}) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 12h7" />
    </>,
    p,
  );

export const IconInfo = (p: Props = {}) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5" />
      <path d="M12 7.6h.01" />
    </>,
    p,
  );

export const IconEdit = (p: Props = {}) =>
  svg(
    <>
      <path d="M4 20h4l10-10-4-4L4 16Z" />
      <path d="M13.5 6.5l4 4" />
    </>,
    p,
  );

export const IconCheck = (p: Props = {}) => svg(<path d="m5 12.5 4.5 4.5L19 7" />, p);

export const IconEye = (p: Props = {}) =>
  svg(
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>,
    p,
  );

export const IconHistory = (p: Props = {}) =>
  svg(
    <>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
      <path d="M3.5 4.5V9h4.5" />
      <path d="M12 8v4.5l3 1.8" />
    </>,
    p,
  );

export const IconStar = (p: Props = {}) =>
  svg(
    <path d="m12 4 2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.6-4.8 2.6.9-5.4L4.2 9.7l5.4-.8Z" />,
    p,
  );

// ── Les partages (4.6) ───────────────────────────────────────────────────────

/** Un groupe **vu comme des gens**, jamais comme un dossier : c'est une audience. */
export const IconMembers = (p: Props = {}) =>
  svg(
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 5.2a3 3 0 0 1 0 5.6M17.5 14.3A6 6 0 0 1 21 20" />
    </>,
    p,
  );

/** Les nouveautés — ce qui vient d'arriver, pas un compteur de non-lus. */
export const IconFeed = (p: Props = {}) =>
  svg(
    <>
      <path d="M4 5h16M4 12h10M4 19h7" />
      <circle cx="18" cy="17" r="3" />
    </>,
    p,
  );

/** Inviter : une adresse, seul identifiant qu'un humain connaisse d'un autre. */
export const IconInvite = (p: Props = {}) =>
  svg(
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </>,
    p,
  );

/** Quitter un groupe (D16) — la porte, pas la corbeille. */
export const IconLeave = (p: Props = {}) =>
  svg(
    <>
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
      <path d="M15 8l4 4-4 4M19 12H9" />
    </>,
    p,
  );

/** « Où va ma donnée » (D15) — un objet, et les cercles qu'il atteint. */
export const IconShareRoutes = (p: Props = {}) =>
  svg(
    <>
      <circle cx="5" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M7.3 10.8 15.7 7.2M7.3 13.2l8.4 3.6" />
    </>,
    p,
  );

/** Le retrait souverain : couper l'accès, **quelle qu'en soit la raison**. */
export const IconRevoke = (p: Props = {}) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m6.5 6.5 11 11" />
    </>,
    p,
  );

/**
 * ── Les glyphes qui étaient des caractères ───────────────────────────────────
 *
 * `⏳`, `⚠`, `∅`, `☑`, `☐` et `★` étaient posés en texte. Deux défauts, et le
 * second est le pire : sur la plupart des systèmes le sablier et le triangle
 * sortent en **emoji couleur**, au milieu d'un jeu de traits monochromes ; et
 * chacun se dessine dans la police disponible, donc différemment d'une machine à
 * l'autre — un glyphe absent devenant un rectangle vide. Ils rejoignent le jeu.
 */

/** Ce qui n'est pas encore prêt : le dérivé arrive, le worker n'a pas fini. */
export const IconPending = (p: Props = {}) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 1.8" />
    </>,
    p,
  );

/** Ce qui a échoué — et qui n'arrivera pas tout seul. */
export const IconAlert = (p: Props = {}) =>
  svg(
    <>
      <path d="M12 4.5 21 19.5H3Z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </>,
    p,
  );

/** Des octets illisibles : il y a bien un objet, il n'y a pas d'image. */
export const IconNoImage = (p: Props = {}) =>
  svg(
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m4 19 6-6 3 3" />
      <path d="M3 3l18 18" />
    </>,
    p,
  );

/**
 * **Le triangle de lecture** — ce qui dit « ceci est une vidéo » dans une grille.
 *
 * Plein, et c'est le seul de la table à l'être : posé sur une photo, à vingt
 * pixels, un contour se perd dans l'image qu'il annote. La convention est
 * universelle, elle ne demande donc aucune légende.
 */
export const IconPlay = (p: Props = {}) => svg(<path d="M9 6.5 17.5 12 9 17.5Z" fill="currentColor" />, p);

/**
 * **La version écran** — l'image que le serveur produit pour être regardée.
 * Un écran, parce que c'est littéralement ce que le rôle `standard` sert.
 */
export const IconScreen = (p: Props = {}) =>
  svg(
    <>
      <rect x="3" y="4.5" width="18" height="12" rx="2" />
      <path d="M8.5 20h7M12 16.5V20" />
    </>,
    p,
  );

/**
 * **Le fichier d'origine** — un carton d'archive, et le mot est exact : passé
 * trente jours la source dort en Glacier Instant Retrieval, dont la récupération
 * se facture. L'icône dit donc à la fois « c'est l'original » et « ça se paie ».
 */
export const IconOriginal = (p: Props = {}) =>
  svg(
    <>
      <path d="M3 7.5 5 4.5h14l2 3" />
      <rect x="3" y="7.5" width="18" height="12" rx="2" />
      <path d="M10 12h4" />
    </>,
    p,
  );

/** Une case de liste de tâches — cochée ou non, dans les deux clients. */
export const IconCheckbox = (p: Props & { readonly checked?: boolean } = {}) =>
  svg(
    p.checked ? (
      <>
        <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
        <path d="m7.5 12 3 3 6-6" />
      </>
    ) : (
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
    ),
    p,
  );

/** Une étoile **pleine** : c'est une note posée, pas un contour à cocher. */
export const IconStarFilled = (p: Props = {}) =>
  svg(
    <path
      d="m12 4 2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.6-4.8 2.6.9-5.4L4.2 9.7l5.4-.8Z"
      fill="currentColor"
    />,
    p,
  );

/** L'administration : un écusson, jamais un engrenage — ce n'est pas un réglage. */
export const IconShield = (p: Props = {}) =>
  svg(
    <>
      <path d="M12 3.5 19.5 6v5.5c0 4.2-3 7.4-7.5 9-4.5-1.6-7.5-4.8-7.5-9V6Z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </>,
    p,
  );

/** Le point d'entrée du menu d'objet — trois points, la convention universelle. */
export const IconMore = (p: Props = {}) =>
  svg(
    <>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </>,
    p,
  );

/**
 * La présentation d'un dossier, en deux dessins qui **montrent le résultat** et
 * non l'état courant : un bouton qui bascule se nomme par ce qu'il produira.
 */
export const IconGrid = (p: Props = {}) =>
  svg(
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </>,
    p,
  );

export const IconList = (p: Props = {}) =>
  svg(
    <>
      <path d="M4 6.5h16M4 12h16M4 17.5h16" />
    </>,
    p,
  );

/**
 * **La densité de la grille**, dessinée par ce qu'elle produit : une grande
 * vignette, quatre moyennes, neuf petites. C'est le seul jeu du produit où
 * l'icône *est* la mesure — un chiffre aurait demandé de savoir si « 3 » compte
 * les colonnes ou les rangées.
 */
export const IconDensityLarge = (p: Props = {}) =>
  svg(<rect x="3.5" y="3.5" width="17" height="17" rx="2" />, p);

export const IconDensityMedium = (p: Props = {}) =>
  svg(
    <>
      <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13" y="3.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="3.5" y="13" width="7.5" height="7.5" rx="1.5" />
      <rect x="13" y="13" width="7.5" height="7.5" rx="1.5" />
    </>,
    p,
  );

export const IconDensitySmall = (p: Props = {}) =>
  svg(
    <>
      <rect x="3.5" y="3.5" width="4.3" height="4.3" rx="1" />
      <rect x="9.9" y="3.5" width="4.3" height="4.3" rx="1" />
      <rect x="16.2" y="3.5" width="4.3" height="4.3" rx="1" />
      <rect x="3.5" y="9.9" width="4.3" height="4.3" rx="1" />
      <rect x="9.9" y="9.9" width="4.3" height="4.3" rx="1" />
      <rect x="16.2" y="9.9" width="4.3" height="4.3" rx="1" />
      <rect x="3.5" y="16.2" width="4.3" height="4.3" rx="1" />
      <rect x="9.9" y="16.2" width="4.3" height="4.3" rx="1" />
      <rect x="16.2" y="16.2" width="4.3" height="4.3" rx="1" />
    </>,
    p,
  );

/**
 * **L'exposition**, sur une tuile : ce qui a quitté l'espace privé pour un
 * cercle.
 *
 * Le dessin dit un **état**, pas une action à déclencher — et c'est là qu'il se
 * trompait. Il portait trois nœuds reliés, c'est-à-dire le pictogramme universel
 * du **bouton « Partager »** : sur une vignette, il se lisait « appuie ici pour
 * partager » alors qu'il énonce « cette photo est déjà partagée ». Un retour
 * d'usage l'a dit mot pour mot le 2026-08-09.
 *
 * Ce sont donc des **gens** — les mêmes que le rail des partages et que la
 * pastille du mobile (`Icons.group_outlined`) : une audience se dessine par ceux
 * qui la composent, et un groupe de personnes ne ressemble à aucun bouton.
 */
export const IconExposed = (p: Props = {}) =>
  svg(
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 6.2a3 3 0 0 1 0 5.9M17.2 14.4A5.5 5.5 0 0 1 20.5 19" />
    </>,
    p,
  );
